import createRootReducer, {
	type ApplicationState,
} from "reduxStore/rootReducer";
import { rootSaga } from "reduxStore/rootSaga";
import {
	type Action,
	type AnyAction,
	configureStore,
	type Store,
	type ThunkDispatch,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import type { i18n } from "i18next";
import {
	type TypedUseSelectorHook,
	useDispatch,
	useSelector,
} from "react-redux";
import createSagaMiddleware from "redux-saga";
import type { ApiClient } from "utils/fetchClient";

export interface Context {
	api: ApiClient;
	i18n: i18n;
}

export default function getStore(
	initialState: ApplicationState,
	context: Context,
): Store<ApplicationState> {
	// create the redux-saga middleware
	const sagaMiddleware = createSagaMiddleware({
		context,
	});
	const sentryEnhancer = Sentry.createReduxEnhancer();
	const store = configureStore({
		preloadedState: initialState,
		reducer: createRootReducer(),
		enhancers: (defaultEnhancers) => defaultEnhancers.concat(sentryEnhancer),
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware({
				thunk: {
					extraArgument: context,
				},
				serializableCheck: {
					ignoredPaths: ["oidc"],
					ignoredActions: ["redux-oidc/LOADING_USER", "redux-oidc/USER_FOUND"],
				},
			}).concat(sagaMiddleware),
	});
	sagaMiddleware.run(rootSaga);

	return store;
}

export const useAppSelector: TypedUseSelectorHook<ApplicationState> =
	useSelector;
export type DispatchAction<T extends AnyAction = Action> = ThunkDispatch<
	ApplicationState,
	undefined,
	T
>;
export const useThunkDispatch: () => DispatchAction = useDispatch;
